import { useState } from "react";
import InfoTooltip from "@/components/UI/InfoTooltip";
import GuideButton from "@/components/UI/GuideButton";
import DisplayModeToggle from "@/components/UI/DisplayModeToggle";
import SegmentedToggle from "@/components/UI/SegmentedToggle";
import NotAvailableIcon from "@/assets/icons/folder_off.svg?react";
import AlertsPanelLayout from "@/components/Alerts/AlertsPanelLayout";
import { useAlertsQuery } from "@/hooks/useAlertsQuery";
import { isAlerted, isArchived } from "@/components/Insights/utils";
import type { AlertProps, InsightProps } from "@/components/Insights/insights";

// useAlertsQuery attaches the resolved insight to each alert; AlertProps
// itself only carries insight_id.
type EnrichedAlert = AlertProps & { insight?: InsightProps };

export default function Alerts() {
  const { alerts } = useAlertsQuery();
  const [showOnlyAlerted, setshowOnlyAlerted] = useState(false);
  const [compactMode, setCompactMode] = useState(false);

  const alertsToUse = alerts.filter((alert: AlertProps) => !isArchived(alert));

  const hasAlerts = alertsToUse.length > 0;

  const filteredAlerts = showOnlyAlerted
    ? alertsToUse.filter(isAlerted)
    : alertsToUse;
  const mineAlerts = filteredAlerts.filter(
    (alert: EnrichedAlert) => !alert.insight?.is_shared,
  );
  const sharedAlerts = filteredAlerts.filter(
    (alert: EnrichedAlert) => alert.insight?.is_shared,
  );

  return (
    <div className="px-4.5 pt-7.5 mb-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2 ">
          <h1 className="text-[18px] font-bold">Alerts</h1>
          <InfoTooltip
            title="Alerts allow for custom automatic data monitoring across all sources, and alert you when trends move in the wrong direction."
            data-testid="alerts-page-tooltip"
          />
        </div>

        <div className="flex gap-2.5 items-center">
          <DisplayModeToggle
            value={compactMode ? "compact" : "expanded"}
            onChange={(value) => setCompactMode(value === "compact")}
            disabled={!hasAlerts}
            testIdPrefix="alerts"
          />
          <SegmentedToggle
            block
            className="w-37.5 bg-white border-[#00000012] border rounded-sm text-sm"
            options={[
              { label: "All", value: "All" },
              { label: "Alerted", value: "Alerted" },
            ]}
            value={showOnlyAlerted ? "Alerted" : "All"}
            onChange={(value) => setshowOnlyAlerted(value === "Alerted")}
            disabled={!hasAlerts}
          />
          <GuideButton pageName="/wiki/guides/alerts-enhanced" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {mineAlerts.length === 0 && sharedAlerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-[#00000073] text-base gap-2 h-[calc(100vh-200px)]">
            <NotAvailableIcon className="w-6 h-6" />
            {hasAlerts
              ? "No triggered alerts available"
              : "No alerts available"}
          </div>
        ) : (
          <AlertsPanelLayout
            mineAlerts={mineAlerts}
            sharedAlerts={sharedAlerts}
            compactMode={compactMode}
          />
        )}
      </div>
    </div>
  );
}
